/**
 * Tutorial definitions — the data-driven core of the Tutorials feature (#124).
 *
 * A tutorial is a sequence of steps, each pinned to a REAL element on a REAL
 * app page. A step carries:
 *   - `route`  — the live path the tour navigates to before showing the step
 *                (react-router path under /app).
 *   - `anchor` — a `data-tour="<name>"` attribute on a real component, resolved
 *                at runtime via `document.querySelector('[data-tour="…"]')`.
 *   - title / body / placement — the StepCard copy + preferred side.
 *
 * The tour HIGHLIGHTS and EXPLAINS the workflow on the genuine UI — it never
 * clicks buttons, submits forms, or writes data. StepCard copy guides the user
 * ("Click X to …, or Next to continue"); the user stays in control. Anchors
 * that live inside a dialog/sheet only mount once the user opens it — the tour
 * controller waits for them with a timeout and gracefully skips if absent, so a
 * user who clicks Next without opening the dialog is never blocked.
 *
 * Adding a new guided procedure = append a `TutorialDefinition` here + sprinkle
 * the matching `data-tour` attributes on the real components. No engine changes.
 */

/** Which side of the anchor the StepCard prefers to sit on. */
export type StepPlacement = "top" | "bottom" | "left" | "right";

/** Logical grouping shown as a section on the hub. */
export type TutorialGroup = "Set up" | "Monitor" | "Run" | "Analyze";

export interface TutorialStep {
  /** Real app path this step lives on (under /app). The tour navigates here
   *  before resolving the anchor. */
  route: string;
  /** `data-tour` attribute value on the real element to spotlight. */
  anchor: string;
  /** Short imperative title, e.g. "Add a Thing". */
  title: string;
  /** One or two sentences explaining what to do / what just happened. Copy
   *  GUIDES the user to act; the tour itself never acts. */
  body: string;
  /** Preferred StepCard side relative to the anchor. Defaults to "bottom". */
  placement?: StepPlacement;
  /**
   * When true, this step's anchor only exists after the user opens a
   * dialog/sheet (it isn't on the page at rest). The controller still tries to
   * resolve it, but won't warn — and the StepCard centers (guidance-only) if it
   * never appears. Pairs with copy that tells the user what to click.
   */
  optionalAnchor?: boolean;
}

export interface TutorialDefinition {
  /** Stable id — used in the route (`/app/tutorials/:id`) and progress store. */
  id: string;
  /** Title shown on the hub card + player header. */
  title: string;
  /** Hub grouping. */
  group: TutorialGroup;
  /** Rough time-to-complete, in minutes, shown on the hub card. */
  estMin: number;
  /** One-line summary for the hub card. */
  summary: string;
  /** Ordered steps. Keep tight: 4–6 per tour. */
  steps: TutorialStep[];
}

/**
 * Tour 1 — Create a Thing (gateway + a tag). Group: Set up.
 * Real page: /app/gateways (the Gateways page) + AddGatewayDialog sheet.
 */
const createAThing: TutorialDefinition = {
  id: "create-a-thing",
  title: "Create a Thing",
  group: "Set up",
  estMin: 2,
  summary:
    "Register a gateway data source and give it a tag, so its readings start flowing into the platform.",
  steps: [
    {
      route: "/app/gateways",
      anchor: "add-thing-btn",
      title: "Add a gateway",
      body: "This is the real Gateways page. A gateway is what connects a PLC or a sensor to the platform. Click 'Add gateway' to open the wizard, then come back and press Next.",
      placement: "bottom",
    },
    {
      route: "/app/gateways",
      anchor: "add-thing-product",
      title: "Pick a gateway product",
      body: "In the wizard, choose the gateway product you're connecting. This tells the platform which field protocol and defaults to expect. Pick one, then Next.",
      placement: "left",
      optionalAnchor: true,
    },
    {
      route: "/app/gateways",
      anchor: "add-thing-tags",
      title: "Author its tags",
      body: "On the Tags step, add a row for each reading the gateway sends, such as a temperature, a pressure or a flow. Name at least one, then Next.",
      placement: "top",
      optionalAnchor: true,
    },
    {
      route: "/app/gateways",
      anchor: "add-thing-save",
      title: "Create the gateway",
      body: "When you're happy, click 'Create gateway' to register it, and the platform begins polling it. The tour never submits for you; you stay in control.",
      placement: "left",
      optionalAnchor: true,
    },
    {
      route: "/app/gateways",
      anchor: "things-table",
      title: "Your data sources live here",
      body: "Every Thing appears in this table with its tag count and online status. That's it — you've seen how a data source is added.",
      placement: "top",
    },
  ],
};

/**
 * Tour 2 — Commission an Entity. Group: Set up.
 * Real page: /app/twin (process graph) + AddEquipmentWizard sheet.
 *
 * Re-pointed onto the unified "Add equipment" flow (#125), which supersedes the
 * old Commission-only wizard: it pairs the same catalogue pick with creating /
 * attaching the equipment's data source and binding its datapoints, all in one
 * entity-first sheet. The tour id/title stay stable (progress store + route).
 */
const commissionAnEntity: TutorialDefinition = {
  id: "commission-an-entity",
  title: "Commission an Entity",
  group: "Set up",
  estMin: 3,
  summary:
    "Add a piece of equipment to the process graph and bind its datapoints to live gateway tags.",
  steps: [
    {
      route: "/app/twin",
      anchor: "add-equipment-launch",
      title: "Add equipment",
      body: "This is the real process graph. Click 'Add equipment' to open the unified flow — it defines the equipment, sets up its data source, and binds its datapoints in one go. Open it, then press Next. (If you don't see the button, your org may be in read-only or demo mode — just press Next to read along.)",
      placement: "bottom",
    },
    {
      route: "/app/twin",
      anchor: "add-equipment-category",
      title: "Filter by category",
      body: "Equipment is grouped by category — process equipment, lab & instruments, QC, transport. Use the chips (or search) to narrow the catalogue, then pick a template.",
      placement: "bottom",
      optionalAnchor: true,
    },
    {
      route: "/app/twin",
      anchor: "add-equipment-pick",
      title: "Pick the equipment",
      body: "Choose the equipment template you're adding — e.g. an induction furnace — or start a blank custom entity. Its ports and datapoints carry through to the next steps. Click a card, then Next.",
      placement: "right",
      optionalAnchor: true,
    },
    {
      route: "/app/twin",
      anchor: "add-equipment-source",
      title: "Set up its data source",
      body: "Give this equipment a data source: create a new gateway/Thing in-flow (its tag rows are seeded from the entity's datapoints), or attach your org's existing tags and bind on the next step.",
      placement: "left",
      optionalAnchor: true,
    },
    {
      route: "/app/twin",
      anchor: "add-equipment-bind",
      title: "Bind datapoints to tags",
      body: "On the Bind step, the platform autosuggests tags whose data-kind matches each datapoint. 'Accept all' applies every suggestion, or pick per row. Binding is optional.",
      placement: "left",
      optionalAnchor: true,
    },
    {
      route: "/app/twin",
      anchor: "add-equipment-finish",
      title: "Add it",
      body: "Click 'Add equipment' on the final step to create the entity on the graph, its data source, and its bindings together. The tour never submits for you — the action is yours.",
      placement: "left",
      optionalAnchor: true,
    },
  ],
};

/**
 * Tour 3 — Run a heat & trace a part. Group: Run.
 * Real pages: /app/twin (Run heat) → /app/twin/batches (heat row + trace drawer).
 */
const runAHeatAndTrace: TutorialDefinition = {
  id: "run-a-heat-and-trace",
  title: "Run a heat & trace a part",
  group: "Run",
  estMin: 3,
  summary:
    "Run a production heat through the process graph, then trace it to see where it came from and where it went.",
  steps: [
    {
      route: "/app/twin",
      anchor: "process-graph-canvas",
      title: "The process graph",
      body: "This canvas is your plant as a flow of steps — charge, melt, pour, shake-out. A 'heat' travels through it. Click any node to inspect its ports and bindings.",
      placement: "top",
    },
    {
      route: "/app/twin",
      anchor: "run-heat-btn",
      title: "Run a heat",
      body: "Click 'Run heat' to generate one complete heat — batch, lots, castings, moulds, specimens — recording the full genealogy. (Read-only/demo orgs won't see this button; press Next to read along.)",
      placement: "bottom",
    },
    {
      route: "/app/twin/batches",
      anchor: "batches-table",
      title: "Heats land here",
      body: "Now you're on the Batches page. Every heat the platform tracked shows up with its grade, yield and defect attribution. Click a heat row to open its trace.",
      placement: "top",
    },
    {
      route: "/app/twin/batches",
      anchor: "batch-trace",
      title: "Trace the heat",
      body: "The trace drawer shows where-from (the lots that fed this heat) and where-used (the parts it became) — full forward + backward genealogy.",
      placement: "left",
      optionalAnchor: true,
    },
  ],
};

/**
 * Tour 4 — Create a dashboard. Group: Monitor.
 * Real page: /app/dashboards + CreateDashboardDialog sheet.
 *
 * The "New dashboard" sheet authors a dashboard's name + starting template; the
 * widget palette and the per-widget data-source (binding) panel live in the
 * dashboard editor that opens right after — so the create-flow steps stay on
 * /app/dashboards (the editor route is per-id) and the copy points the user
 * into the editor for the palette + binding.
 */
const createADashboard: TutorialDefinition = {
  id: "create-a-dashboard",
  title: "Create a dashboard",
  group: "Monitor",
  estMin: 2,
  summary:
    "Spin up a dashboard, then compose it from the widget palette and bind each widget to live data.",
  steps: [
    {
      route: "/app/dashboards",
      anchor: "dashboard-new-btn",
      title: "New dashboard",
      body: "This is the real Dashboards gallery. Click 'New dashboard' to open the create sheet, then come back and press Next. (No button? Your org may be read-only — just press Next to read along.)",
      placement: "bottom",
    },
    {
      route: "/app/dashboards",
      anchor: "dashboard-name",
      title: "Name it",
      body: "Give the dashboard a clear name — e.g. 'Melt shop overview'. This is how it'll show up in the gallery. Type a name, then Next.",
      placement: "bottom",
      optionalAnchor: true,
    },
    {
      route: "/app/dashboards",
      anchor: "dashboard-template",
      title: "Start blank or clone a pack",
      body: "Choose a starting point: a blank grid you fill yourself, or a clone of a pack-shipped dashboard you can tweak. Pick one, then Next.",
      placement: "top",
      optionalAnchor: true,
    },
    {
      route: "/app/dashboards",
      anchor: "dashboard-create",
      title: "Create it",
      body: "Click 'Create' to make the dashboard and drop into its editor. The tour never submits for you — the click is yours.",
      placement: "left",
      optionalAnchor: true,
    },
    {
      route: "/app/dashboards",
      anchor: "dashboards-grid",
      title: "Compose & bind in the editor",
      body: "Your dashboards live here. Open one and hit 'Edit' to get the 'Add widget' palette — drag in KPIs, charts and alarm feeds — then click any widget to open its data-source panel and bind it to a tag or entity port. That's a dashboard, end to end.",
      placement: "top",
    },
  ],
};

/**
 * Tour 5 — Set an alarm. Group: Monitor.
 * Real page: /app/alarms (Rules tab) + RuleEditor sheet.
 */
const setAnAlarm: TutorialDefinition = {
  id: "set-an-alarm",
  title: "Set an alarm",
  group: "Monitor",
  estMin: 2,
  summary:
    "Author an alarm rule that fires when a tag crosses a threshold, and route it to a channel.",
  steps: [
    {
      route: "/app/alarms",
      anchor: "alarm-rules-tab",
      title: "Open the Rules tab",
      body: "This is the real Alarms page — the first tab is the live alarm feed. Click the 'Rules' tab to manage what fires those alarms, then Next.",
      placement: "bottom",
    },
    {
      route: "/app/alarms",
      anchor: "alarm-new-rule",
      title: "New rule",
      body: "Click 'New rule' to open the rule editor, then come back and press Next. (Read-only orgs won't see this — just press Next to read along.)",
      placement: "left",
      optionalAnchor: true,
    },
    {
      route: "/app/alarms",
      anchor: "alarm-rule-target",
      title: "Pick the tag to watch",
      body: "Choose the tag this rule monitors — search your org's tags by Thing or name. This is the signal the threshold is checked against. Pick one, then Next.",
      placement: "top",
      optionalAnchor: true,
    },
    {
      route: "/app/alarms",
      anchor: "alarm-rule-condition",
      title: "Set the threshold",
      body: "Define when it fires: an operator (greater than, less than…), the value, and an optional 'for N seconds' to avoid flapping. Set these, then Next.",
      placement: "top",
      optionalAnchor: true,
    },
    {
      route: "/app/alarms",
      anchor: "alarm-rule-save",
      title: "Create the rule",
      body: "Set a severity and channels (in-app, email, SMS…), then click 'Create rule' to start evaluating it against incoming readings. The tour never submits for you.",
      placement: "left",
      optionalAnchor: true,
    },
  ],
};

/**
 * Tour 6 — Read correlations. Group: Analyze.
 * Real page: /app/twin/correlations (matrix + defect drill-down tabs).
 */
const readCorrelations: TutorialDefinition = {
  id: "read-correlations",
  title: "Read correlations",
  group: "Analyze",
  estMin: 3,
  summary:
    "Read the Pearson matrix, see which parameters drive your defects, then drill into a specific failure.",
  steps: [
    {
      route: "/app/twin/correlations",
      anchor: "correlation-heatmap",
      title: "The correlation matrix",
      body: "Each cell is the Pearson r between two parameters — red moves together, blue moves opposite, grey is no relationship. Click any cell to see its scatter below. Then Next.",
      placement: "right",
    },
    {
      route: "/app/twin/correlations",
      anchor: "correlation-ranking",
      title: "What's driving rejections",
      body: "This panel pulls the matrix's most useful slice — the parameters most strongly linked to your rejection (defect) rate — ranked by strength. The yield drivers, at a glance. Then Next.",
      placement: "left",
    },
    {
      route: "/app/twin/correlations",
      anchor: "correlation-drilldown-tab",
      title: "Drill into one defect",
      body: "Correlation shows what moves together; the 'Defect drill-down' tab explains a specific failure. Click it to switch tabs, then Next.",
      placement: "bottom",
    },
    {
      route: "/app/twin/correlations",
      anchor: "correlation-defect-scope",
      title: "Pick a defect to explain",
      body: "Click a defect chip — e.g. porosity — to ask 'why did this fail?'. The platform finds which upstream equipment, material lot or out-of-spec parameter is over-represented in that failure population versus the good one. (Live-data feature; the chips appear once a tracked org is connected.)",
      placement: "bottom",
      optionalAnchor: true,
    },
  ],
};

/**
 * Tour 7 — Generate a report. Group: Analyze.
 * Real page: /app/twin/reports (GeneratedReportPanel + saved gallery).
 */
const generateAReport: TutorialDefinition = {
  id: "generate-a-report",
  title: "Generate a report",
  group: "Analyze",
  estMin: 2,
  summary:
    "Roll up your heats into a deterministic statistical report — yield, rejection and defect tallies.",
  steps: [
    {
      route: "/app/twin/reports",
      anchor: "report-granularity",
      title: "Pick a granularity",
      body: "This is the report generator on the real Reports page. Choose how to group the roll-up — heat-wise, daily, weekly or monthly. Pick one, then Next.",
      placement: "bottom",
    },
    {
      route: "/app/twin/reports",
      anchor: "report-generate",
      title: "Generate it",
      body: "Click 'Generate' to compute the report live from your heat data. It's deterministic math — no AI — so the same data always yields the same numbers. Click it, then Next.",
      placement: "left",
    },
    {
      route: "/app/twin/reports",
      anchor: "report-results",
      title: "Read the results",
      body: "The KPI strip and table land here — yield %, rejection %, failed heats and top defects per bucket — and 'Export CSV' saves the table. (Generate first; if you skipped it, just press Next.)",
      placement: "top",
      optionalAnchor: true,
    },
    {
      route: "/app/twin/reports",
      anchor: "reports-gallery",
      title: "Save & reuse report templates",
      body: "Below the generator, the gallery holds pack-shipped and your own saved reports — Pareto, SPC, defect-attribution. Clone a pack template to customize it. That's reporting, end to end.",
      placement: "top",
      optionalAnchor: true,
    },
  ],
};

/** All tutorials, in hub display order. */
export const TUTORIALS: readonly TutorialDefinition[] = [
  createAThing,
  commissionAnEntity,
  createADashboard,
  setAnAlarm,
  runAHeatAndTrace,
  readCorrelations,
  generateAReport,
];

/** Look up a tutorial by id (used by the player route + controller). */
export function getTutorial(id: string): TutorialDefinition | undefined {
  return TUTORIALS.find((t) => t.id === id);
}

/** Group order for the hub sections. */
export const GROUP_ORDER: readonly TutorialGroup[] = [
  "Set up",
  "Monitor",
  "Run",
  "Analyze",
];
